import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AppSidebar from '../../../components/AppSidebar';
import AppHeader from '../../../components/AppHeader';
import PageHeader from '../../../components/PageHeader';
import { toast, ToastContainer } from 'react-toastify';
import Pagination from '../../../components/Pagination';
import CommonTable from '../../../components/CommonTable';
import ConfirmModal from '../../../modals/ConfirmModal';
import DesignationService from '../../../services/DesignationService';

const Designations = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState('10');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [rows, setRows] = useState([]);
    const columns = [
        { Header: '', accessor: '' },
        { Header: 'Name', accessor: 'name' },
        { Header: 'Description', accessor: 'description' },
        { Header: 'Department', accessor: 'department.name' },
        { Header: 'Status', accessor: 'status' },
      ];

      const fetchData = useCallback(async () => {
        try {
          setLoading(true);
          const response = await DesignationService.getAll();
          if (response?.status === 200 && response?.data?.success) {
            setRows(response?.data?.data?.designation);
          } else {
            console.error('Error in fetching designation data:', response);
            setRows([]);
            toast.error(response?.data?.message || 'Failed to fetch designations. Please try again later.');
          }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch designations. Please try again later.');
            setRows([]);
        } finally {
          setLoading(false);
        }
      }, []);

      useEffect(() => {
        fetchData();
      }, [fetchData]);

      const filteredRows = useMemo(() => {
        return rows.filter(row => {
          const matchesSearch = searchTerm === '' ||
            row?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row?.description?.toLowerCase().includes(searchTerm.toLowerCase());
          return matchesSearch;
       });
      }, [searchTerm,rows]);

      const paginatedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = itemsPerPage === 'All' ? filteredRows.length : startIndex + parseInt(itemsPerPage, 10);
        return filteredRows.slice(startIndex, endIndex);
      }, [filteredRows, currentPage, itemsPerPage]);
    
      const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
      };

      const handleItemsPerPageChange = (e) => {
        setItemsPerPage(e.target.value);
        setCurrentPage(1);
      };
    
      const handleSelectAllChange = (e) => {
        const isChecked = e.target.checked;
        if (isChecked && filteredRows.length > 0) {
          setSelectedRows(filteredRows.map(row => row.id));
          setShowConfirmModal(true);
        } else {
          setSelectedRows([]);
          setShowConfirmModal(false);
        }
      };
    
      const handleCheckboxChange = (rowId) => {
        setSelectedRows(prevSelectedRows =>
          prevSelectedRows.includes(rowId)
            ? prevSelectedRows.filter(id => id !== rowId)
            : [...prevSelectedRows, rowId]
        );
    
        if (!selectedRows.includes(rowId)) {
          setShowConfirmModal(true);
        } else if (selectedRows.length === 1) {
          setShowConfirmModal(false);
        }
      };
    
      const handleDelete = async (ids) => {
        try {
    
          for (const id of ids) {
            await DesignationService.delete(id);
          }
          setRows(prevRows => prevRows.filter(row => !ids.includes(row.id)));
          setShowConfirmModal(false);
          setSelectedRows([]);
          toast.success('Designations deleted successfully!');
        } catch (error) {
          console.error('Error deleting records:', error);
          toast.error(error?.response?.data?.message || error?.message || 'Failed to delete designations. Please try again later.');
        }
      };
   
  const totalPages = Math.ceil(filteredRows?.length / (itemsPerPage === 'All' ? filteredRows?.length : parseInt(itemsPerPage, 10)));

  return (
    <>
      <AppSidebar />
      <div className="main-content">
        <AppHeader />
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12 ">
              <PageHeader title="Designations" button_label="Create" link_to="create" 
                className="btn btn-sm btn-primary me-2" />
              <div className="tab-content">
                <div className="tab-pane fade show active" id="contactsListPane" role="tabpanel" aria-labelledby="contactsListTab">
                  <div className="card">
                    <div className="card-header">
                      <div className="row align-items-center">
                        <div className="col">
                          <form>
                            <div className="input-group input-group-flush input-group-merge input-group-reverse">
                              <input
                                className="form-control list-search"
                                type="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                              />
                              <span className="input-group-text">
                                <i className="fe fe-search" />
                              </span>
                            </div>
                          </form>
                        </div>
                        <div className="col-auto me-n3">
                          <form>
                            <select
                              className="form-select form-select-sm form-control-flush"
                              value={itemsPerPage}
                              onChange={handleItemsPerPageChange}
                            >
                              <option value="5">5 per page</option>
                              <option value="10">10 per page</option>
                              <option value="All">All Records</option>
                            </select>
                          </form>
                        </div>
                      </div>
                    </div>
                    {/* Common Table component*/}
                    <CommonTable
                    loading={loading}
                    paginatedRows={paginatedRows}
                    rows = {rows}
                    columns={columns}
                    onSelectAllChange={handleSelectAllChange}
                    selectedRows={selectedRows}
                    onCheckboxChange={handleCheckboxChange}
                    setSelectedRows = {setSelectedRows}
                    setShowConfirmModal ={setShowConfirmModal}
                    basePath="designations"
                    showLimitedOptions={true}
                  />
                  <ToastContainer />

                    {/* Confirm Modal */}
                    {showConfirmModal && (
                      <ConfirmModal
                        show={showConfirmModal}
                        onCancel={() => {
                          setShowConfirmModal(false);
                          setSelectedRows([]);
                        }}
                        onConfirm={() => handleDelete(selectedRows)}
                        id={selectedRows}
                        heading='Delete Designation'
                        content='Are you sure you want to delete this designation?'
                        type="designations"
                      />
                    )}
                    {/* Pagination Component */}
                    <Pagination
                     currentPage={currentPage}
                     totalPages={totalPages}
                     setCurrentPage={setCurrentPage}
                     />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Designations;
